import { computed, ref } from "vue";
import {
  DRAGGER_ROW_HEIGHT,
  HEADER_ROW_HEIGHT,
} from "@/components/canvas/layouts/constants";
import { DEFAULT_RULER_CONFIG } from "@/drawing/overlay/types";
import { RULER_SIZE_CONFIGS } from "@/drawing/overlay/rulerSizes";
import { hasTouchScreen, rulerGrabPolicy } from "@/drawing/overlay/rulerGrab";
import {
  getCanvasIconScale,
  getCanvasTextScale,
} from "@/composable/useCanvasUiScale";
import { useTextPanelCollapse } from "@/composable/useTextPanelCollapse";
import { isFullscreen } from "./CanvasViewState";
import { activeCatalogue } from "./CatalogueTabsState";
import DraggerMenuState, { handlesScaleFactor } from "./DraggerMenuState";
import { drawerWidth, isDrawerOpen } from "./DrawerWidthState";
import Settings from "./Settings";
import { rulerSize } from "./RulerState";
import {
  toolbarColumnsMode,
  visibleToolbarGroups,
} from "./ToolbarVisibilityState";
import {
  chooseToolbarColumns,
  toolbarHeight,
  toolbarWidth as widthForColumns,
} from "@/utils/toolbar-layout";

/**
 * Géométrie de l'outil dessin, source unique pour tout ce qui se cale sous
 * l'en-tête (canvas, barre d'outils, volets, barre photo…) ou à côté de la
 * barre d'outils (pavé numérique, dock de l'outil texte).
 *
 * Deux tailles d'interface (modale Affichage, cf. useCanvasUiScale) : celle
 * des ICÔNES (barre d'outils, bulles des onglets, barres flottantes, ligne des
 * notes ; classe `canvas-icon-scaled`) et celle des TEXTES (en-tête, volets,
 * bandeaux ; classe `canvas-text-scaled`). Un élément zoomé exprime ses
 * longueurs en px de BASE de sa famille ; ce qui passe d'une famille à l'autre
 * passe par les px ÉCRAN (suffixe `Px`) : px écran = px de base × échelle.
 * Calculées plutôt que mesurées : une mesure déjà mise à l'échelle serait
 * multipliée une seconde fois.
 */

/** Les onglets de catalogue ne tiennent pas sur la 1re ligne : 2e ligne. */
export const areCatalogueTabsWrapped = ref(false);

/** Popups de l'en-tête ouvertes (cf. useHeaderPopoverLock). */
export const openHeaderPopovers = ref(0);

/** Hauteur de l'en-tête (px de base des textes : l'en-tête est un texte). */
export const headerHeight = computed(() => {
  if (isFullscreen.value) return 0;
  return HEADER_ROW_HEIGHT * (areCatalogueTabsWrapped.value ? 2 : 1);
});

/** Hauteur de l'en-tête (px écran). */
export const headerHeightPx = computed(
  () => headerHeight.value * getCanvasTextScale(),
);

/** Ligne des notes : repliée en plein écran ou quand aucun catalogue n'est ouvert. */
export const isDraggerRowOpen = computed(
  () => !isFullscreen.value && activeCatalogue.value !== null,
);

/** Hauteur de la ligne des notes (px écran) : des boutons, à la taille des icônes. */
const draggerRowHeightPx = computed(() =>
  isDraggerRowOpen.value ? DRAGGER_ROW_HEIGHT * getCanvasIconScale() : 0,
);

/** Haut de la zone de dessin (px écran). */
export const canvasTopPx = computed(
  () => headerHeightPx.value + draggerRowHeightPx.value,
);

/** Haut de la zone de dessin, pour un élément à la taille des icônes. */
export const canvasTopForIcons = computed(
  () => canvasTopPx.value / getCanvasIconScale(),
);

/** Haut de la zone de dessin, pour un élément à la taille des textes. */
export const canvasTopForTexts = computed(
  () => canvasTopPx.value / getCanvasTextScale(),
);

// ── Règles (dessinées dans le canvas, en px écran non zoomés) ─────────────────

const isTouchScreen = hasTouchScreen();

/** Épaisseur choisie des règles (px du canvas = px écran). */
function rulerThickness() {
  return RULER_SIZE_CONFIGS[rulerSize.value];
}

/**
 * Épaisseur occupée par une règle le long du bord du plan (px de base des
 * icônes : la barre d'outils et le pavé s'y calent), poignée ronde comprise :
 * centrée sur la règle, elle la dépasse quand le réglage « Taille des
 * poignées » est grand. Sans poignées (mode stylet, écran sans tactile : cf.
 * rulerGrabPolicy), la règle seule. 0 quand les règles sont masquées.
 */
export const rulerClearance = computed(() => {
  if (!DraggerMenuState.rulerVisible) return 0;
  const { thickness } = rulerThickness();
  const { handleSize } = DEFAULT_RULER_CONFIG;
  const { showHandles } = rulerGrabPolicy(Settings.onlyStylusOn, isTouchScreen);
  const handle = showHandles
    ? handleSize * handlesScaleFactor(DraggerMenuState.handlesScale)
    : 0;
  return Math.max(thickness, (thickness + handle) / 2) / getCanvasIconScale();
});

// ── Onglets des volets (bulles flottantes à droite, cf. DrawerTabBubbles) ────
// Bulles : px de base des icônes. Volet : px de base des textes.

/** Taille d'une bulle d'onglet (carrée). */
export const DRAWER_TAB_SIZE = 52;
/** Espace entre deux bulles. */
export const DRAWER_TABS_GAP = 6;
/** Écart entre le haut du plan et la 1re bulle. */
export const DRAWER_TABS_TOP_GAP = 12;
/** Écart entre les bulles et le bord droit du plan (ou sa règle verticale). */
const DRAWER_TABS_EDGE_GAP = 8;
/**
 * Débord de la poignée du volet hors de son bord gauche (px de base des
 * textes, comme le volet : cf. DrawerResizeHandle).
 */
const DRAWER_HANDLE_OVERHANG = 12;
/** Air entre cette poignée et les bulles (px de base des icônes). */
const DRAWER_HANDLE_TABS_GAP = 4;

/**
 * Écart entre le volet ouvert et les bulles (px écran) : la poignée du volet,
 * à la taille des textes, puis un peu d'air, à celle des icônes.
 */
const drawerPanelGapPx = computed(
  () =>
    DRAWER_HANDLE_OVERHANG * getCanvasTextScale() +
    DRAWER_HANDLE_TABS_GAP * getCanvasIconScale(),
);

/**
 * Taille de la fenêtre (px écran), suivie pour borner le volet à l'écran et
 * choisir le nombre de colonnes de la barre d'outils.
 */
const viewportWidth = ref(
  typeof window === "undefined" ? 0 : window.innerWidth,
);
const viewportHeight = ref(
  typeof window === "undefined" ? 0 : window.innerHeight,
);
if (typeof window !== "undefined") {
  window.addEventListener("resize", () => {
    viewportWidth.value = window.innerWidth;
    viewportHeight.value = window.innerHeight;
  });
}

// ── Barre d'outils (flottante, collée au bord gauche, cf. CanvasToolbar) ─────
// px de base des icônes.

/** Écart entre la barre et le haut du plan, ou sa règle horizontale en bas. */
export const TOOLBAR_EDGE_GAP = 8;

/** Bas de la zone de la barre (depuis le bas de l'écran). */
export const toolbarBottom = computed(
  () => rulerClearance.value + TOOLBAR_EDGE_GAP,
);

const toolbarAvailableHeight = computed(
  () =>
    (viewportHeight.value - canvasTopPx.value) / getCanvasIconScale() -
    TOOLBAR_EDGE_GAP -
    toolbarBottom.value,
);

const toolbarGroupSizes = computed(() =>
  visibleToolbarGroups.value.map((group) => group.tools.length),
);

/**
 * Deux colonnes quand une seule ne tient pas dans la hauteur du plan, ou
 * quand le réglage les impose (modale Affichage).
 */
export const toolbarColumns = computed(() =>
  chooseToolbarColumns(
    toolbarGroupSizes.value,
    toolbarAvailableHeight.value,
    toolbarColumnsMode.value,
  ),
);

/** Même en trois colonnes, la barre dépasse : elle se cale alors en haut. */
export const isToolbarOverflowing = computed(
  () =>
    toolbarHeight(toolbarGroupSizes.value, toolbarColumns.value) >
    toolbarAvailableHeight.value,
);

/** Largeur de la barre, 0 quand tous les outils sont masqués. */
export const toolbarWidth = computed(() =>
  toolbarGroupSizes.value.length ? widthForColumns(toolbarColumns.value) : 0,
);

/** Largeur de la barre (px écran). */
export const toolbarWidthPx = computed(
  () => toolbarWidth.value * getCanvasIconScale(),
);

/**
 * Bord gauche de ce qui se pose en bas, à droite de la barre d'outils : dock
 * replié de l'outil texte et poignée de la règle horizontale, qu'il recouvre.
 * Même écart que la barre au bord du plan.
 */
export const toolbarSideStart = computed(
  () => toolbarWidth.value + TOOLBAR_EDGE_GAP,
);

/** Idem, en px écran (pixels du canvas). */
export const toolbarSideStartPx = computed(
  () => toolbarSideStart.value * getCanvasIconScale(),
);

/**
 * Change dès que la barre change de forme ou de place : les panneaux
 * d'options ouverts se recalent sur leur outil.
 */
export const toolbarLayoutKey = computed(() =>
  [
    toolbarColumns.value,
    toolbarGroupSizes.value.join("-"),
    canvasTopPx.value,
    toolbarBottom.value,
    viewportHeight.value,
  ].join("|"),
);

/**
 * Largeur affichée du volet (px de base des textes) : celle choisie par
 * l'utilisateur, mais jamais au point de pousser les bulles hors de l'écran ou
 * sur la barre d'outils (volet élargi en paysage puis tablette tournée en
 * portrait, tailles d'interface agrandies…). La largeur mémorisée, elle, n'est
 * pas modifiée.
 */
export const effectiveDrawerWidth = computed(() => {
  const bubblesPx =
    (DRAWER_TAB_SIZE + DRAWER_TABS_EDGE_GAP) * getCanvasIconScale() +
    drawerPanelGapPx.value;
  const availablePx = viewportWidth.value - toolbarWidthPx.value - bubblesPx;
  return Math.max(
    0,
    Math.min(drawerWidth.value, availablePx / getCanvasTextScale()),
  );
});

/**
 * Distance entre la règle verticale et le bord droit du plan (px du canvas,
 * donc écran), suivie par DrawerTabBubbles auprès des règles.
 */
export const verticalRulerRightOffset = ref(0);

/** En deçà (px de base des icônes), la règle verticale est « collée » au bord droit. */
const RULER_NEAR_EDGE = 16;
/** Écart entre la règle verticale et les bulles qui s'en écartent. */
const RULER_TABS_GAP = 4;

/**
 * Règle verticale affichée et collée (ou presque) au bord droit : les bulles
 * s'en écartent pour la laisser voir. Ailleurs, elles restent contre le bord.
 */
const isVerticalRulerAtRightEdge = computed(
  () =>
    DraggerMenuState.rulerVisible &&
    verticalRulerRightOffset.value <= RULER_NEAR_EDGE * getCanvasIconScale(),
);

/**
 * Distance des bulles au bord droit de l'écran (px de base des icônes). Le
 * volet ouvert les pousse : elles se calent alors contre son bord gauche ;
 * sinon contre le bord du plan, ou juste à gauche de la règle verticale quand
 * elle y est.
 */
export const drawerTabsRight = computed(() => {
  const iconScale = getCanvasIconScale();
  if (isDrawerOpen.value) {
    const drawerPx = effectiveDrawerWidth.value * getCanvasTextScale();
    return (drawerPx + drawerPanelGapPx.value) / iconScale;
  }
  if (isVerticalRulerAtRightEdge.value) {
    return (
      (verticalRulerRightOffset.value + rulerThickness().thickness) /
        iconScale +
      RULER_TABS_GAP
    );
  }
  return DRAWER_TABS_EDGE_GAP;
});

/**
 * Place occupée à droite du plan par les bulles (et le volet ouvert), en px
 * écran : les éléments posés en haut à droite (marqueurs photo) s'écartent
 * d'autant.
 */
export const rightOverlayClearancePx = computed(
  () =>
    (drawerTabsRight.value + DRAWER_TAB_SIZE + DRAWER_TABS_EDGE_GAP) *
    getCanvasIconScale(),
);

// ── Coin bas gauche (pavé numérique, cf. NumericKeypadModal) ───────────────
// px de base des icônes.

/** Hauteur du dock replié de l'outil texte, en bas à gauche (cf. TextToolDock). */
const TEXT_DOCK_HEIGHT = 57;

const { isCollapsed: isTextDockVisible } = useTextPanelCollapse();

/**
 * Ce qui occupe le bas du plan à droite de la barre d'outils : la règle
 * horizontale et sa poignée, ou le dock replié de l'outil texte.
 */
export const bottomLeftClearance = computed(() =>
  isTextDockVisible.value ? TEXT_DOCK_HEIGHT : rulerClearance.value,
);

// ── Barre de sélection (en bas, cf. CanvasToolbarOptions) ───────────────────

/**
 * Largeur (px de base des icônes) sous laquelle la barre de sélection ne
 * tient plus sur une ligne : téléphone en portrait.
 */
const SELECTION_SHEET_MAX_WIDTH = 560;

/**
 * Écran trop étroit : la barre de sélection passe ses outils à la ligne au
 * lieu de déborder.
 */
export const isSelectionSheet = computed(
  () => viewportWidth.value / getCanvasIconScale() < SELECTION_SHEET_MAX_WIDTH,
);
